/**
 * FeatureCollectionDetails helper utilities.
 *
 * Pure functions extracted from FeatureCollectionDetails for testability.
 */

import {
  MAX_LOGICSETS_PER_XMC,
  MAX_TIMERS_PER_XMC,
  MAX_CAN_INPUTS,
  MAX_SWITCH_INDICATORS,
} from 'utils/constants'
import { checkIsEligibleEcu } from 'utils/featureValidation/checks'

// WP-3 3.6: Only XMC 3 and XMC 4 features are eligible for collections
export const ALLOWED_COLLECTION_ECUS = ['XMC3', 'XMC4', 'XMC 3', 'XMC 4']
// Normalized list used by checkIsEligibleEcu and ECU chip filters (no spaces, matches CHECPLUS_MODULE_NAMES)
export const ALLOWED_COLLECTION_ECUS_NORMALIZED = ['XMC3', 'XMC4']

// Resource limits sourced from the canonical constants module.
// Exported for consumers that previously imported from this file.
export const RESOURCE_LIMITS = {
  MAX_LOGICSETS_PER_XMC,
  MAX_TIMERS_PER_XMC,
  MAX_CAN_INPUTS,
  MAX_SWITCH_INDICATORS,
}

export const getCollectionMutationErrorMessage = (error, fallbackMessage) =>
  typeof error?.message === 'string' && error.message.trim()
    ? error.message
    : fallbackMessage

/**
 * Parse a diagnostic variant string into a numeric value for comparison.
 * Handles "App_XXXX", "0xXXXX", and raw hex suffix "XXXX" formats.
 *
 * @param {string} variant - e.g. "App_0706", "0x0706", "0706"
 * @returns {number|null} The parsed integer, or null if the input is invalid.
 */
export const parseVariantNumber = (variant) => {
  if (!variant || typeof variant !== 'string') return null
  let hexPart = variant.trim()
  if (hexPart.startsWith('App_')) {
    hexPart = hexPart.slice(4)
  } else if (hexPart.toLowerCase().startsWith('0x')) {
    hexPart = hexPart.slice(2)
  }
  if (hexPart.length === 0) return null
  const num = parseInt(hexPart, 16)
  return Number.isNaN(num) ? null : num
}

/**
 * Normalize an ECU module name by removing whitespace for comparison.
 * Handles null/undefined safely.
 *
 * @param {string} moduleName - e.g. "XMC 3", "XMC3"
 * @returns {string} Normalized name with no spaces, e.g. "XMC3"
 */
export const normalizeModuleName = (moduleName) => {
  if (!moduleName || typeof moduleName !== 'string') return ''
  return moduleName.replace(/\s/g, '')
}

/**
 * Parse a pipe-separated varMinCompat string into a map of module names to variant numbers.
 * Format: "0115|0115" where each entry corresponds to XMC 3, XMC 4, etc.
 *
 * @param {string} varMinCompat - e.g. "0115", "0115|0115"
 * @returns {Object<string, number>} Map of normalized module name to parsed variant number.
 *   e.g. { XMC3: 277, XMC4: 277 }
 */
export const parseVarMinCompatMap = (varMinCompat) => {
  const map = {}
  if (!varMinCompat || typeof varMinCompat !== 'string') return map
  const variants = varMinCompat.split('|')
  variants.forEach((v, i) => {
    const moduleNum = 3 + i // XMC numbering starts at 3
    const parsed = parseVariantNumber(v.trim())
    if (parsed !== null) {
      map[`XMC${moduleNum}`] = parsed
    }
  })
  return map
}

/**
 * Check if a feature's minCompatibleVariant exceeds the collection's varMinCompat
 * for its module. Returns true if the feature is incompatible.
 *
 * @param {object} record - Feature record with moduleName and minCompatibleVariant
 * @param {string} varMinCompat - Collection's varMinCompat string (pipe-separated)
 * @returns {boolean} true if incompatible, false otherwise
 */
export const isFeatureIncompatibleWithCollection = (record, varMinCompat) => {
  if (!varMinCompat || !record || !record.minCompatibleVariant) return false
  const compatMap = parseVarMinCompatMap(varMinCompat)
  const moduleName = normalizeModuleName(record.moduleName)
  const collectionVariant = compatMap[moduleName]
  if (collectionVariant == null) return false
  const featureVariant = parseVariantNumber(String(record.minCompatibleVariant))
  if (featureVariant === null) return false
  return featureVariant > collectionVariant
}

/**
 * Filter search results to only include features compatible with the collection.
 * Features whose minCompatibleVariant exceeds the collection's varMinCompat are excluded.
 * Non-Active features are also excluded.
 *
 * @param {Array} results - Array of feature records
 * @param {string} varMinCompat - Collection's varMinCompat string (pipe-separated)
 * @returns {Array} Filtered results
 */
export const filterCompatibleSearchResults = (results, varMinCompat) => {
  if (!results || !Array.isArray(results)) return undefined
  if (!varMinCompat || typeof varMinCompat !== 'string') return undefined
  const compatMap = parseVarMinCompatMap(varMinCompat)
  return results.filter((record) => {
    const status = (record.status || '').toLowerCase()
    if (status !== 'active') return false
    if (!record.minCompatibleVariant) return true
    const moduleName = normalizeModuleName(record.moduleName)
    const collectionVariant = compatMap[moduleName]
    if (collectionVariant == null) return true
    const featureVariant = parseVariantNumber(
      String(record.minCompatibleVariant)
    )
    if (featureVariant === null) return true
    return featureVariant <= collectionVariant
  })
}

/**
 * Check if a feature record is on an eligible ECU for collection.
 * Returns null if eligible, error string if not.
 *
 * Delegates to the canonical checkIsEligibleEcu in utils/featureValidation/checks
 * to avoid duplicating the validation logic and its error message.
 */
export const isEligibleEcu = (record) =>
  checkIsEligibleEcu(record, ALLOWED_COLLECTION_ECUS_NORMALIZED)

/**
 * Check if a feature record has Active status.
 * Returns null if Active, error string if not.
 */
export const isActiveStatus = (record) =>
  record.status?.toLowerCase() === 'active'
    ? null
    : 'Only Features with "Active" status can be added'

export const getValidDynamicLogicCounts = (dynamicCount) => {
  const { currentLogicCount, maxLogicCount } = dynamicCount || {}
  return typeof currentLogicCount === 'number' &&
    Number.isFinite(currentLogicCount) &&
    currentLogicCount >= 0 &&
    typeof maxLogicCount === 'number' &&
    Number.isFinite(maxLogicCount) &&
    maxLogicCount > 0
    ? { currentLogicCount, maxLogicCount }
    : null
}

/**
 * Resolve the logic-set limit supplied by capacity preflight. The maximum is
 * useful independently of the server-calculated current count, while invalid
 * or unavailable preflight data must retain the conservative client fallback.
 */
export const getEffectiveLogicSetLimit = (dynamicCount, fallbackLimit) => {
  const { maxLogicCount } = dynamicCount || {}
  return typeof maxLogicCount === 'number' &&
    Number.isFinite(maxLogicCount) &&
    maxLogicCount > 0
    ? maxLogicCount
    : fallbackLimit
}

/**
 * Calculate ECU resource limit violations from a working set of features.
 * Returns an array of violation objects.
 *
 * @param {Array} features - Working set of features
 * @param {object} [limits] - Override resource limits (for testing)
 * @returns {Array<{ moduleName: string, type: string, count: number, max: number }>}
 */
export const getEcuResourceViolations = (
  features,
  limits = RESOURCE_LIMITS,
  moduleDynamicCounts = {}
) => {
  const violations = []
  const activeFeatures = features.filter((f) => !f.isMarkedForDeletion)
  const grouped = activeFeatures.reduce((acc, f) => {
    // Normalize module names before grouping so 'XMC3' and 'XMC 3' are counted
    // together. Spaced and unspaced formats can coexist when persisted features
    // and newly searched features arrive from different API responses.
    const mod = normalizeModuleName(f.moduleName)
    if (!mod) return acc
    if (!acc[mod]) acc[mod] = []
    acc[mod].push(f)
    return acc
  }, {})

  Object.entries(grouped).forEach(([moduleName, moduleFeatures]) => {
    // Compute a human-readable display name from the normalized module name.
    // normalizeModuleName strips spaces ('XMC 3' → 'XMC3'); we add one back
    // before digits for readability ('XMC3' → 'XMC 3').
    const moduleDisplayName = moduleName.replace(/([A-Za-z])(\d)/, '$1 $2')
    const logicSets = moduleFeatures.reduce(
      (sum, f) => sum + (f.complexQueryCount || 0),
      0
    )
    const timers = moduleFeatures.reduce(
      (sum, f) => sum + (f.timerQueryCount || 0),
      0
    )
    const canInputs = new Set(
      moduleFeatures.flatMap((f) => f.canInputs || []).filter(Boolean)
    ).size
    const switchIndicators = new Set(
      moduleFeatures.flatMap((f) => f.switchIndicators || []).filter(Boolean)
    ).size

    const dynamicLogicCounts = getValidDynamicLogicCounts(
      moduleDynamicCounts[moduleName]
    )
    const effectiveLogicSets = dynamicLogicCounts
      ? dynamicLogicCounts.currentLogicCount
      : logicSets
    const effectiveLogicLimit = getEffectiveLogicSetLimit(
      moduleDynamicCounts[moduleName],
      limits.MAX_LOGICSETS_PER_XMC
    )

    if (effectiveLogicSets > effectiveLogicLimit) {
      violations.push({
        moduleName,
        moduleDisplayName,
        type: 'Logic Sets',
        count: effectiveLogicSets,
        max: effectiveLogicLimit,
      })
    }
    if (timers > limits.MAX_TIMERS_PER_XMC) {
      violations.push({
        moduleName,
        moduleDisplayName,
        type: 'Timers',
        count: timers,
        max: limits.MAX_TIMERS_PER_XMC,
      })
    }
    if (canInputs > limits.MAX_CAN_INPUTS) {
      violations.push({
        moduleName,
        moduleDisplayName,
        type: 'CAN Inputs',
        count: canInputs,
        max: limits.MAX_CAN_INPUTS,
      })
    }
    if (switchIndicators > limits.MAX_SWITCH_INDICATORS) {
      violations.push({
        moduleName,
        moduleDisplayName,
        type: 'Switch Indicators',
        count: switchIndicators,
        max: limits.MAX_SWITCH_INDICATORS,
      })
    }
  })
  return violations
}
